import { Typography } from "core/typography/Typography";
import React, { useContext, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import "./MissionToolsForm.scss";
import PropTypes from "prop-types";
import InputText from "core/inputs/input-text/InputText";
import Button from "core/buttons/button/Button";
import Icon from "core/icon/Icon";
import {
  FORM_INFO,
  JOB_CATEGORY_LABEL,
  JOB_LOCKED_PLACEHOLDER,
} from "constants/forms";
import {
  MISSION_STATUS_FILTERS,
  TAG_TYPES,
} from "constants/constants";
import {
  getTranslatedList,
  onDeleteUpdateOptions,
} from "components/form/CommonInputs";
import InputSearchTag from "core/inputs/input-search/InputSearchTag";
import { getJobCategoryOptions } from "utils/helper";
import _ from "lodash";
import InputCheckList from "core/inputs/input-check-list/InputCheckList";
import InfoContext from "pages/InfoContext";
import InputDate from "core/inputs/input-date/InputDate";
import { getJobsInCategories } from "utils/functions";

function MissionToolsForm({
  lang,
  jobList,
  sectorList,
  getFilters,
  setDisplayMission,
}) {
  const {
    setMissionTitle,
    setMissionJob,
    setMissionClientSector,
    setMissionStatusfilter,
    setIsMissionsStarOnly,
    setIsMissionsImpactOnly,
    missionTitleFilter,
    setMissionTitleFilter,
    jobFilter,
    setJobFilter,
    jobCategoryFilter,
    setJobCategoryFilter,
    jobCategoryList,
    getCategoryJobIds,
    setMissionJobCategory,
    sectorFilter,
    setSectorFilter,
    missionStatus,
    setMissionStatus,
    star,
    setStar,
    impact,
    setImpact,
    btnColor,
    setMissionsByFilters,
    setMissionsEndedAfterFilter,
    dateFilter,
    setDateFilter,
  } = useContext(InfoContext);
  useEffect(() => {
    setDisplayMission(false);
  }, []);

  useEffect(() => {
    const updateColor = (btn, isActive, activeColor, setter) => {
      setter({
        ...btn,
        color: isActive ? activeColor : btnColor.default,
      });
    };

    const CTA_btns = [
      {
        btn: star,
        isActive: star.isStar,
        activeColor: btnColor.isStar,
        setter: setStar,
      },
      {
        btn: impact,
        isActive: impact.isImpact,
        activeColor: btnColor.isImpact,
        setter: setImpact,
      },
    ];

    CTA_btns.forEach(({ btn, isActive, activeColor, setter }) =>
      updateColor(btn, isActive, activeColor, setter)
    );
  }, [star.isStar, impact.isImpact]);

  // The selected category narrows the job filter.
  const selectedCategoryId = jobCategoryFilter?.[0]?.value;
  const allowedJobIds = selectedCategoryId
    ? getCategoryJobIds(selectedCategoryId)
    : [];
  const jobOptions = getJobsInCategories(jobList, allowedJobIds);

  // Drop a job the category no longer offers — removing the category included,
  // which leaves nothing selectable (redmine 12939). A job kept there would be
  // sent with every search while the locked field gave no way to remove it.
  useEffect(() => {
    if (!jobFilter?.length) return;
    if (!selectedCategoryId) {
      setJobFilter([]);
      return;
    }
    // Only prune against a referential that has actually landed: it arrives
    // after the filters restored from storage, and an empty one would read as
    // "this category offers nothing" and wipe a legitimate pair.
    if (!jobCategoryList?.length) return;
    const kept = getJobsInCategories(jobFilter, allowedJobIds);
    if (kept.length !== jobFilter.length) setJobFilter(kept);
  }, [selectedCategoryId, jobCategoryList]);

  const search = () => {
    const filters = {
      query: missionTitleFilter,
      domain_category_id: jobCategoryFilter[0]?.value,
      domain_id: jobFilter[0]?.value,
      sector_id: sectorFilter?.map((e) => e.value),
      status: missionStatus,
      star_only: star.isStar,
      impact: impact.isImpact,
      include_description: true,
      end_from_date_or_null: dateFilter,
    };
    getFilters({ filters });
  };

  const handleSubmit = () => {
    setMissionTitle(missionTitleFilter);
    setMissionsEndedAfterFilter(dateFilter);
    setMissionJob(jobFilter);
    setMissionJobCategory(jobCategoryFilter);
    setMissionClientSector(sectorFilter);
    setMissionStatusfilter(missionStatus);
    setIsMissionsStarOnly(star.isStar);
    setIsMissionsImpactOnly(impact.isImpact);
    setDisplayMission(false);
    setMissionsByFilters({});
    search();
  };

  return (
    <div className="mission-tools-wrapper">
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={
          <FormattedMessage
            id="mission_tools"
            defaultMessage="RECHERCHER DES MISSIONS"
          />
        }
      />
      <div className="tool-txt">
        <FormattedMessage
          id="desc_mission_tools"
          defaultMessage="Minimum de 3 lettres pour effectuer votre recherche, ou d'un métier/secteur.
          La recherche est possible avec plusieurs mots clés. ( Le séparateur '|' est disponible pour améliorer votre recherche)"
        />
      </div>
      <div className="section-1">
        <div className="main-block">
          <div className="left-block">
            <div className="line-1">
              <div className="cell-padding">
                <InputText
                  errors={{}}
                  label={{
                    id: "filter_mission_tools_title",
                    defaultMessage:
                      "Nom de la mission (mot dans le nom ou dans le descriptif de la mission)",
                  }}
                  placeholder={{
                    id: "filter_mission_title_placeholder",
                    defaultMessage: "Mots clés",
                  }}
                  id="mission-title-filter"
                  onChange={(e) => setMissionTitleFilter(e.target.value)}
                  value={missionTitleFilter}
                />
              </div>
              <div className="cell-padding">
                <InputDate
                  lang={lang}
                  label={{
                    id: "filter_missions_ended_after",
                    defaultMessage: "Missions terminées après le",
                  }}
                  id="missions-ended-after"
                  onChange={(e) => setDateFilter(e.target.value)}
                  value={dateFilter}
                />
              </div>
            </div>
            <div className="line-2">
              <div className="cell-padding">
                <InputSearchTag
                  name="domain_category_id"
                  errors={{}}
                  label={JOB_CATEGORY_LABEL}
                  limitChoice={1}
                  onDelete={(selected) =>
                    setJobCategoryFilter(
                      onDeleteUpdateOptions(jobCategoryFilter, selected)
                    )
                  }
                  options={getJobCategoryOptions({
                    lang,
                    list: jobCategoryList,
                    type: TAG_TYPES.FUNCTIONS,
                  })}
                  popup={FORM_INFO.dav_function_category.popup}
                  selectedOptions={jobCategoryFilter}
                  setValue={setJobCategoryFilter}
                  type={TAG_TYPES.FUNCTIONS}
                  id="job-category-filter"
                />
              </div>
              <div className="cell-padding">
                <InputSearchTag
                  name="domain_id"
                  errors={{}}
                  label={{
                    id: "filter_mission_tools_job",
                    defaultMessage: "Métier de la mission",
                  }}
                  limitChoice={1}
                  onDelete={(selected) => {
                    setJobFilter((old) =>
                      old.filter((item) => item.value !== selected)
                    );
                  }}
                  options={getTranslatedList(jobOptions, lang)}
                  popup={FORM_INFO.dav_function.popup}
                  // The category filter comes first: it is what narrows this
                  // list (redmine 12939).
                  disabled={!selectedCategoryId}
                  placeholder={
                    selectedCategoryId ? undefined : JOB_LOCKED_PLACEHOLDER
                  }
                  selectedOptions={jobFilter}
                  setValue={setJobFilter}
                  type={TAG_TYPES.FUNCTIONS}
                  id="job-filter"
                />
              </div>
              <div className="cell-padding">
                <InputSearchTag
                  name="sector_id"
                  errors={{}}
                  label={{
                    id: "filter_mission_tools_sector",
                    defaultMessage: "Secteur Client de la mission",
                  }}
                  limitChoice={20}
                  onDelete={(selected) => {
                    setSectorFilter((old) =>
                      old.filter((item) => item.value !== selected)
                    );
                  }}
                  options={getTranslatedList(
                    sectorList,
                    lang,
                    TAG_TYPES.SECTORS
                  )}
                  popup={FORM_INFO.sector.popup}
                  selectedOptions={sectorFilter}
                  exclusiveValue={0}
                  setValue={setSectorFilter}
                  type={TAG_TYPES.SECTORS}
                  id="sector-filter"
                />
              </div>
            </div>
          </div>
          <div className="right-block">
            <div className="cell-padding">
              <label className="label">
                <FormattedMessage
                  id="filter_mission_tools_status"
                  defaultMessage="Statut de la mission"
                />
              </label>
              <div className="checkList">
                <InputCheckList
                  onChange={(vals) => {
                    setMissionStatus(vals || []);
                  }}
                  value={missionStatus}
                  infoList={MISSION_STATUS_FILTERS}
                  readOnly={false}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="bottom-section">
          <div className="search-buttons-container">
            <div className="star-search">
              <button
                className={`star-wrapper selected button-style`}
                onClick={() => setStar({ ...star, isStar: !star?.isStar })}
              >
                <div className="star">
                  <Icon
                    icon="star2"
                    color={star?.color}
                    width={"22"}
                    height={"21"}
                  />
                </div>
                <FormattedMessage
                  id="star-search"
                  defaultMessage="Montrer uniquement les missions Star"
                />
              </button>
            </div>
            <div className="impact-search">
              <button
                className={`impact-wrapper selected button-style`}
                onClick={() =>
                  setImpact({ ...impact, isImpact: !impact?.isImpact })
                }
              >
                <div className="impact">
                  <Icon
                    icon="leaf"
                    color={impact?.color}
                    width={"22"}
                    height={"26"}
                  />
                </div>
                <FormattedMessage
                  id="impact-search"
                  defaultMessage="Impact ODD"
                />
              </button>
            </div>
          </div>
          <div className="div-buttons">
            <Button
              color="btn-medium gray-700"
              onClick={() => handleSubmit()}
              id="button-search"
            >
              <FormattedMessage
                id="search_missions_button_tools"
                defaultMessage="Rechercher les missions"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
MissionToolsForm.propTypes = {
  lang: PropTypes.string.isRequired,
  jobList: PropTypes.array.isRequired,
  sectorList: PropTypes.array.isRequired,
  getFilters: PropTypes.func.isRequired,
  setDisplayMission: PropTypes.func.isRequired,
};

export default MissionToolsForm;
